import React from 'react';

/**
 * Premium hero animation: income → hub → 4 accounts.
 *
 * - Pure SVG + CSS keyframes (no deps, ~4kb gz)
 * - 5-second loop, subtle motion, dark-theme native
 * - Reinforces the "route through a system" positioning without explainer text
 */
export default function IncomeFlowHero() {
  return (
    <div
      className="relative w-full flex items-center justify-center select-none"
      aria-hidden="true"
      data-testid="income-flow-hero"
    >
      {/* Scoped animation styles */}
      <style>{`
        @keyframes pt-drop {
          0%   { transform: translateY(-24px); opacity: 0; }
          15%  { opacity: 1; }
          45%  { transform: translateY(36px); opacity: 1; }
          55%  { transform: translateY(36px); opacity: 0; }
          100% { transform: translateY(36px); opacity: 0; }
        }
        @keyframes pt-hub-pulse {
          0%, 40%   { transform: scale(1);    filter: brightness(1); }
          55%       { transform: scale(1.06); filter: brightness(1.25); }
          75%, 100% { transform: scale(1);    filter: brightness(1); }
        }
        @keyframes pt-branch {
          0%, 55%  { stroke-dashoffset: 140; opacity: 0.3; }
          70%      { stroke-dashoffset: 0;   opacity: 1; }
          95%,100% { stroke-dashoffset: 0;   opacity: 0.55; }
        }
        @keyframes pt-node {
          0%, 60%  { opacity: 0.35; transform: translateY(4px); }
          78%      { opacity: 1;    transform: translateY(0); }
          100%     { opacity: 0.9;  transform: translateY(0); }
        }
        @keyframes pt-aligned {
          0%, 80%  { opacity: 0; transform: translateY(6px); }
          92%,100% { opacity: 1; transform: translateY(0); }
        }
        .pt-anim { animation-duration: 5s; animation-iteration-count: infinite; animation-timing-function: cubic-bezier(.2,.7,.3,1); }
        .pt-drop  { animation-name: pt-drop; }
        .pt-pulse { animation-name: pt-hub-pulse; transform-origin: center; transform-box: fill-box; }
        .pt-path  { stroke-dasharray: 140; animation-name: pt-branch; }
        .pt-node  { transform-origin: center; transform-box: fill-box; animation-name: pt-node; }
        .pt-node-1 { animation-delay: 0s; }
        .pt-node-2 { animation-delay: 120ms; }
        .pt-node-3 { animation-delay: 240ms; }
        .pt-node-4 { animation-delay: 360ms; }
        .pt-path-2 { animation-delay: 80ms; }
        .pt-path-3 { animation-delay: 160ms; }
        .pt-path-4 { animation-delay: 240ms; }
        @media (prefers-reduced-motion: reduce) {
          .pt-anim { animation: none !important; }
          .pt-path { stroke-dashoffset: 0 !important; opacity: 0.7 !important; }
          .pt-node { opacity: 1 !important; transform: none !important; }
          .pt-drop { opacity: 0 !important; }
        }
      `}</style>

      <svg
        viewBox="0 0 420 200"
        className="w-full max-w-md h-auto"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="pt-stroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="pt-hub-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e3a8a" />
            <stop offset="100%" stopColor="#111827" />
          </linearGradient>
          <radialGradient id="pt-coin" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#93c5fd" />
            <stop offset="100%" stopColor="#3b82f6" />
          </radialGradient>
        </defs>

        {/* Falling coin → hub */}
        <g className="pt-anim pt-drop" style={{ transformOrigin: 'center' }}>
          <circle cx="210" cy="24" r="9" fill="url(#pt-coin)" opacity="0.95" />
          <text
            x="210"
            y="28"
            textAnchor="middle"
            fontSize="11"
            fontWeight="700"
            fill="#0b1220"
            fontFamily="ui-sans-serif, system-ui"
          >$</text>
        </g>

        {/* Hub (Income / Business Account) */}
        <g className="pt-anim pt-pulse">
          <rect
            x="156"
            y="70"
            width="108"
            height="36"
            rx="10"
            fill="url(#pt-hub-fill)"
            stroke="#3b82f6"
            strokeOpacity="0.7"
            strokeWidth="1.25"
          />
          <text
            x="210"
            y="88"
            textAnchor="middle"
            fontSize="10"
            fontWeight="700"
            fill="#e5e7eb"
            fontFamily="ui-sans-serif, system-ui"
            letterSpacing="0.5"
          >INCOME HUB</text>
          <text
            x="210"
            y="100"
            textAnchor="middle"
            fontSize="8.5"
            fill="#9ca3af"
            fontFamily="ui-sans-serif, system-ui"
          >Business Account</text>
        </g>

        {/* Branch paths hub → 4 nodes */}
        <path d="M 180 106 C 180 130, 90  135, 66  154"  className="pt-anim pt-path"            stroke="url(#pt-stroke)" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M 200 106 C 200 130, 160 140, 150 154"  className="pt-anim pt-path pt-path-2"  stroke="url(#pt-stroke)" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M 220 106 C 220 130, 260 140, 270 154"  className="pt-anim pt-path pt-path-3"  stroke="url(#pt-stroke)" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M 240 106 C 240 130, 330 135, 354 154"  className="pt-anim pt-path pt-path-4"  stroke="url(#pt-stroke)" strokeWidth="1.5" strokeLinecap="round" />

        {/* 4 account nodes */}
        <AccountNode x={32}  y={154} label="Personal" delayClass="pt-node-1" />
        <AccountNode x={116} y={154} label="Business" delayClass="pt-node-2" />
        <AccountNode x={236} y={154} label="Taxes"    delayClass="pt-node-3" />
        <AccountNode x={320} y={154} label="Savings"  delayClass="pt-node-4" />

        {/* End-state confirmation */}
        <g className="pt-anim pt-aligned" style={{ transformOrigin: 'center' }}>
          <circle cx="198" cy="193" r="2.5" fill="#10b981" />
          <text
            x="205"
            y="196"
            fontSize="9"
            fontWeight="600"
            fill="#10b981"
            fontFamily="ui-sans-serif, system-ui"
            letterSpacing="0.5"
          >SYSTEM ALIGNED</text>
        </g>
      </svg>
    </div>
  );
}

function AccountNode({ x, y, label, delayClass }) {
  return (
    <g className={`pt-anim pt-node ${delayClass}`}>
      <rect
        x={x}
        y={y}
        width="68"
        height="24"
        rx="7"
        fill="#0f172a"
        stroke="#334155"
        strokeWidth="1"
      />
      <text
        x={x + 34}
        y={y + 16}
        textAnchor="middle"
        fontSize="9.5"
        fontWeight="600"
        fill="#cbd5e1"
        fontFamily="ui-sans-serif, system-ui"
      >{label}</text>
    </g>
  );
}
